import React, { useState } from 'react';
import styled from 'styled-components';
import { Button, Input, Text } from '@teals/ui-web';
import { useAuth } from '../../hooks';

/* ---------- types ---------- */
type AuthView = 'signin' | 'signup' | 'verify';

/* ---------- styled components ---------- */
const FormContainer = styled.div`
  width: 100%;
  max-width: 380px;
`;

const AuthTabs = styled.div`
  display: flex;
  background: var(--bg-elevated);
  border-radius: 10px;
  padding: 4px;
  margin-bottom: 2rem;
`;

const AuthTab = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 0.625rem;
  border-radius: 8px;
  text-align: center;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ $active }) => ($active ? 'var(--text-primary)' : 'var(--text-muted)')};
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  background: ${({ $active }) => ($active ? 'var(--bg-surface)' : 'transparent')};
  box-shadow: ${({ $active }) => ($active ? 'var(--shadow-sm)' : 'none')};
  font-family: inherit;
`;

const FormHeaderWrapper = styled.div`
  margin-bottom: 2rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const SocialBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--bg-surface);
  color: var(--text-primary);
  font-family: inherit;
  font-size: 0.9375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;

  &:hover {
    background: var(--bg-elevated);
    border-color: var(--border-strong);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: var(--text-muted);
  font-size: 0.8125rem;
  margin: 0.5rem 0;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--border);
  }
`;

const FormRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  color: var(--text-secondary);
  cursor: pointer;

  input[type='checkbox'] {
    accent-color: var(--accent);
  }
`;

const ForgotLink = styled.a`
  font-size: 0.8125rem;
  color: var(--accent);
  font-weight: 500;
  cursor: pointer;
`;

const TermsText = styled.p`
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const ErrorBanner = styled.div`
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 0.85rem;
  margin-bottom: 16px;
`;

const SuccessBanner = styled.div`
  background: rgba(34, 197, 94, 0.1);
  color: #22c55e;
  border: 1px solid rgba(34, 197, 94, 0.2);
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 0.85rem;
  margin-bottom: 16px;
`;

const AuthFooter = styled.div`
  margin-top: 2rem;
  text-align: center;
  font-size: 0.875rem;
  color: var(--text-muted);

  a {
    color: var(--accent);
    font-weight: 500;
    cursor: pointer;
  }
`;

const ResendLink = styled.a`
  font-size: 0.8125rem;
  color: var(--accent);
  font-weight: 500;
  cursor: pointer;
  display: inline-block;
  margin-top: 0.5rem;
`;

/* ---------- Google SVG icon ---------- */
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

/* ---------- component ---------- */
const Auth: React.FC = () => {
  const [view, setView] = useState<AuthView>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const { login, signup, confirmSignup, resendCode, isLoading, error } = useAuth();

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    await login(email, password);
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await signup(email, password, fullName);
    if (result) {
      setSignupEmail(result);
      setSignupPassword(password);
      setView('verify');
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    await confirmSignup(signupEmail, verificationCode, signupPassword);
  };

  const handleResendCode = async () => {
    await resendCode(signupEmail);
  };

  const switchToSignup = () => setView('signup');
  const switchToSignin = () => { setView('signin'); setVerificationCode(''); };

  const titles: Record<AuthView, { title: string; subtitle: string }> = {
    signin: { title: 'Welcome back', subtitle: 'Sign in to your Lumina account' },
    signup: { title: 'Get started', subtitle: 'Create your Lumina account in seconds' },
    verify: { title: 'Verify your email', subtitle: `We sent a verification code to ${signupEmail}` },
  };

  return (
    <FormContainer>
      {view !== 'verify' && (
        <AuthTabs>
          <AuthTab $active={view === 'signin'} onClick={switchToSignin}>
            Sign In
          </AuthTab>
          <AuthTab $active={view === 'signup'} onClick={switchToSignup}>
            Create Account
          </AuthTab>
        </AuthTabs>
      )}

      <FormHeaderWrapper>
        <Text variant="h3" style={{ marginBottom: '0.5rem' }}>{titles[view].title}</Text>
        <Text variant="body2" color="var(--text-muted)">{titles[view].subtitle}</Text>
      </FormHeaderWrapper>

      {error && <ErrorBanner>{error}</ErrorBanner>}

      {/* SIGN IN */}
      {view === 'signin' && (
        <>
          <Form onSubmit={handleSignIn}>
            <SocialBtn type="button">
              <GoogleIcon />
              Continue with Google
            </SocialBtn>
            <Divider>or</Divider>
            <Input
              label="Email address"
              type="email"
              placeholder="you@company.com"
              autoComplete="email"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              required
              fullWidth
            />
            <Input
              label="Password"
              type="password"
              placeholder="Enter your password"
              autoComplete="current-password"
              value={password}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
              required
              fullWidth
            />
            <FormRow>
              <CheckboxLabel>
                <input type="checkbox" /> Remember me
              </CheckboxLabel>
              <ForgotLink>Forgot password?</ForgotLink>
            </FormRow>
            <Button type="submit" variant="primary" fullWidth isLoading={isLoading}>
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </Form>
          <AuthFooter>
            Don&apos;t have an account?{' '}
            <a onClick={switchToSignup}>Create one</a>
          </AuthFooter>
        </>
      )}

      {/* SIGN UP */}
      {view === 'signup' && (
        <>
          <Form onSubmit={handleSignUp}>
            <SocialBtn type="button">
              <GoogleIcon />
              Continue with Google
            </SocialBtn>
            <Divider>or</Divider>
            <Input
              label="Full name"
              type="text"
              placeholder="Jane Smith"
              autoComplete="name"
              value={fullName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFullName(e.target.value)}
              required
              fullWidth
            />
            <Input
              label="Email address"
              type="email"
              placeholder="you@company.com"
              autoComplete="email"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              required
              fullWidth
            />
            <Input
              label="Password"
              type="password"
              placeholder="Create a password"
              autoComplete="new-password"
              value={password}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
              required
              fullWidth
            />
            <TermsText>
              By creating an account you agree to our Terms of Service and Privacy Policy.
            </TermsText>
            <Button type="submit" variant="primary" fullWidth isLoading={isLoading}>
              {isLoading ? 'Creating account...' : 'Create Account'}
            </Button>
          </Form>
          <AuthFooter>
            Already have an account?{' '}
            <a onClick={switchToSignin}>Sign in</a>
          </AuthFooter>
        </>
      )}

      {/* VERIFY */}
      {view === 'verify' && (
        <>
          <SuccessBanner>
            A verification code has been sent to your email.
          </SuccessBanner>
          <Form onSubmit={handleVerify}>
            <Input
              label="Verification code"
              type="text"
              placeholder="Enter the 6-digit code"
              autoComplete="one-time-code"
              value={verificationCode}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setVerificationCode(e.target.value)}
              required
              fullWidth
            />
            <Button type="submit" variant="primary" fullWidth isLoading={isLoading}>
              {isLoading ? 'Verifying...' : 'Verify & Sign In'}
            </Button>
          </Form>
          <div style={{ textAlign: 'center' }}>
            <ResendLink onClick={handleResendCode}>
              Resend verification code
            </ResendLink>
          </div>
          <AuthFooter>
            <a onClick={switchToSignin}>Back to Sign In</a>
          </AuthFooter>
        </>
      )}
    </FormContainer>
  );
};

export default Auth;
